import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, PlusCircle, Edit2, Trash2, Download, Eye, FileText, Check, X, AlertCircle, Upload } from 'lucide-react';

export default function HorseCard({ horse, isExpanded, onToggle, API_URL, isDemoMode, setError, setSuccess, onDocumentChange }) {
  // Data states
  const [feeding, setFeeding] = useState([]);
  const [medications, setMedications] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form states
  const [showFeedingForm, setShowFeedingForm] = useState(false);
  const [showMedForm, setShowMedForm] = useState(false);
  const [showSuppForm, setShowSuppForm] = useState(false);
  const [showDocForm, setShowDocForm] = useState(false);
  const [editingFeedId, setEditingFeedId] = useState(null);
  const [editingMedId, setEditingMedId] = useState(null);
  const [editingSuppId, setEditingSuppId] = useState(null);
  const [viewingDocument, setViewingDocument] = useState(null);

  // Feeding form fields
  const [feedType, setFeedType] = useState('');
  const [feedTypeDetail, setFeedTypeDetail] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState('');
  const [schedule, setSchedule] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');

  // Medication form fields
  const [medName, setMedName] = useState('');
  const [dosage, setDosage] = useState('');
  const [medFrequency, setMedFrequency] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [purpose, setPurpose] = useState('');
  const [prescribingVet, setPrescribingVet] = useState('');
  const [medActive, setMedActive] = useState(true);

  // Supplement form fields
  const [suppName, setSuppName] = useState('');
  const [suppDosage, setSuppDosage] = useState('');
  const [suppFrequency, setSuppFrequency] = useState('');
  const [suppPurpose, setSuppPurpose] = useState('');
  const [suppActive, setSuppActive] = useState(true);

  // Document form fields
  const [docType, setDocType] = useState('coggins');
  const [selectedFile, setSelectedFile] = useState(null);
  const [expirationDate, setExpirationDate] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    // Always load summary counts for collapsed view
    loadCounts();
  }, [horse.id]);

  useEffect(() => {
    if (isExpanded) {
      loadAllData();
    }
  }, [isExpanded, horse.id]);

  const loadCounts = async () => {
    // Load just the counts for the summary, not full data
    try {
      await Promise.all([
        loadFeeding(),
        loadMedications(),
        loadSupplements(),
        loadDocuments()
      ]);
    } catch (err) {
      console.error('Failed to load counts:', err);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadFeeding(),
      loadMedications(),
      loadSupplements(),
      loadDocuments()
    ]);
    setLoading(false);
  };

  const loadFeeding = async () => {
    try {
      const response = await fetch(`${API_URL}/horses/${horse.id}/feeding`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Convert single object to array for backwards compatibility
        setFeeding(Array.isArray(data) ? data : (data.id ? [data] : []));
      }
    } catch (err) {
      console.error('Failed to load feeding:', err);
    }
  };

  const loadMedications = async () => {
    try {
      const response = await fetch(`${API_URL}/horses/${horse.id}/medications`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMedications(data);
      }
    } catch (err) {
      console.error('Failed to load medications:', err);
    }
  };

  const loadSupplements = async () => {
    try {
      const response = await fetch(`${API_URL}/horses/${horse.id}/supplements`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSupplements(data);
      }
    } catch (err) {
      console.error('Failed to load supplements:', err);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await fetch(`${API_URL}/horses/${horse.id}/documents`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  };

  // Get summary for collapsed view
  const getSummary = () => {
    const parts = [];
    if (feeding.length > 0) parts.push(`${feeding.length} feeding${feeding.length > 1 ? 's' : ''}`);
    const activeMeds = medications.filter(m => m.active).length;
    if (activeMeds > 0) parts.push(`${activeMeds} med${activeMeds > 1 ? 's' : ''}`);
    const activeSupps = supplements.filter(s => s.active).length;
    if (activeSupps > 0) parts.push(`${activeSupps} supp${activeSupps > 1 ? 's' : ''}`);
    if (documents.length > 0) parts.push(`${documents.length} doc${documents.length > 1 ? 's' : ''}`);
    return parts.length > 0 ? parts.join(' • ') : 'No care instructions yet';
  };

  // Feeding handlers
  const handleSaveFeeding = async () => {
    if (isDemoMode) {
      setError('Cannot modify data in demo mode');
      return;
    }

    try {
      // Combine feed type and detail (e.g., "Feed - Sweet Feed" or "Hay - Alfalfa")
      const fullFeedType = feedTypeDetail ? `${feedType} - ${feedTypeDetail}` : feedType;
      const data = { feed_type: fullFeedType, amount, frequency, schedule, special_instructions: specialInstructions };
      const method = editingFeedId ? 'PUT' : 'POST';
      const url = editingFeedId
        ? `${API_URL}/horses/${horse.id}/feeding/${editingFeedId}`
        : `${API_URL}/horses/${horse.id}/feeding`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setSuccess(editingFeedId ? 'Feeding updated' : 'Feeding added');
        resetFeedingForm();
        loadFeeding();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || 'Failed to save feeding');
        console.error('Save feeding error:', response.status, errorData);
      }
    } catch (err) {
      setError('Error saving feeding: ' + err.message);
      console.error('Save feeding exception:', err);
    }
  };

  const resetFeedingForm = () => {
    setFeedType('');
    setFeedTypeDetail('');
    setAmount('');
    setFrequency('');
    setSchedule('');
    setSpecialInstructions('');
    setEditingFeedId(null);
    setShowFeedingForm(false);
  };

  const editFeeding = (feed) => {
    // Split "Feed - Sweet Feed" back into feedType and feedTypeDetail
    const feedTypeParts = (feed.feed_type || '').split(' - ');
    setFeedType(feedTypeParts[0] || '');
    setFeedTypeDetail(feedTypeParts[1] || '');
    setAmount(feed.amount || '');
    setFrequency(feed.frequency || '');
    setSchedule(feed.schedule || '');
    setSpecialInstructions(feed.special_instructions || '');
    setEditingFeedId(feed.id);
    setShowFeedingForm(true);
  };

  const deleteFeeding = async (id) => {
    if (isDemoMode) {
      setError('Cannot modify data in demo mode');
      return;
    }
    if (!window.confirm('Delete this feeding entry?')) return;

    try {
      const response = await fetch(`${API_URL}/horses/${horse.id}/feeding/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        setSuccess('Feeding deleted');
        loadFeeding();
      }
    } catch (err) {
      setError('Error deleting feeding');
    }
  };

  // Medication handlers
  const handleSaveMed = async () => {
    if (isDemoMode) {
      setError('Cannot modify data in demo mode');
      return;
    }

    try {
      const data = {
        medication_name: medName,
        dosage,
        frequency: medFrequency,
        start_date: startDate,
        end_date: endDate,
        purpose,
        prescribing_vet: prescribingVet,
        active: medActive
      };

      const method = editingMedId ? 'PUT' : 'POST';
      const url = editingMedId
        ? `${API_URL}/horses/${horse.id}/medications/${editingMedId}`
        : `${API_URL}/horses/${horse.id}/medications`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setSuccess(editingMedId ? 'Medication updated' : 'Medication added');
        resetMedForm();
        loadMedications();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || 'Failed to save medication');
        console.error('Save medication error:', response.status, errorData);
      }
    } catch (err) {
      setError('Error saving medication: ' + err.message);
      console.error('Save medication exception:', err);
    }
  };

  const resetMedForm = () => {
    setMedName('');
    setDosage('');
    setMedFrequency('');
    setStartDate('');
    setEndDate('');
    setPurpose('');
    setPrescribingVet('');
    setMedActive(true);
    setEditingMedId(null);
    setShowMedForm(false);
  };

  const editMed = (med) => {
    setMedName(med.medication_name || '');
    setDosage(med.dosage || '');
    setMedFrequency(med.frequency || '');
    setStartDate(med.start_date ? med.start_date.split('T')[0] : '');
    setEndDate(med.end_date ? med.end_date.split('T')[0] : '');
    setPurpose(med.purpose || '');
    setPrescribingVet(med.prescribing_vet || '');
    setMedActive(med.active);
    setEditingMedId(med.id);
    setShowMedForm(true);
  };

  const deleteMed = async (id) => {
    if (isDemoMode) {
      setError('Cannot modify data in demo mode');
      return;
    }
    if (!window.confirm('Delete this medication?')) return;

    try {
      const response = await fetch(`${API_URL}/horses/${horse.id}/medications/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        setSuccess('Medication deleted');
        loadMedications();
      }
    } catch (err) {
      setError('Error deleting medication');
    }
  };

  // Supplement handlers
  const handleSaveSupp = async () => {
    if (isDemoMode) {
      setError('Cannot modify data in demo mode');
      return;
    }

    try {
      const data = {
        supplement_name: suppName,
        dosage: suppDosage,
        frequency: suppFrequency,
        purpose: suppPurpose,
        active: suppActive
      };

      const method = editingSuppId ? 'PUT' : 'POST';
      const url = editingSuppId
        ? `${API_URL}/horses/${horse.id}/supplements/${editingSuppId}`
        : `${API_URL}/horses/${horse.id}/supplements`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setSuccess(editingSuppId ? 'Supplement updated' : 'Supplement added');
        resetSuppForm();
        loadSupplements();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || 'Failed to save supplement');
        console.error('Save supplement error:', response.status, errorData);
      }
    } catch (err) {
      setError('Error saving supplement: ' + err.message);
      console.error('Save supplement exception:', err);
    }
  };

  const resetSuppForm = () => {
    setSuppName('');
    setSuppDosage('');
    setSuppFrequency('');
    setSuppPurpose('');
    setSuppActive(true);
    setEditingSuppId(null);
    setShowSuppForm(false);
  };

  const editSupp = (supp) => {
    setSuppName(supp.supplement_name || '');
    setSuppDosage(supp.dosage || '');
    setSuppFrequency(supp.frequency || '');
    setSuppPurpose(supp.purpose || '');
    setSuppActive(supp.active);
    setEditingSuppId(supp.id);
    setShowSuppForm(true);
  };

  const deleteSupp = async (id) => {
    if (isDemoMode) {
      setError('Cannot modify data in demo mode');
      return;
    }
    if (!window.confirm('Delete this supplement?')) return;

    try {
      const response = await fetch(`${API_URL}/horses/${horse.id}/supplements/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        setSuccess('Supplement deleted');
        loadSupplements();
      }
    } catch (err) {
      setError('Error deleting supplement');
    }
  };

  // Document handlers
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File must be less than 10MB');
        e.target.value = '';
        return;
      }
      const allowedTypes = ['.pdf', '.png', '.jpg', '.jpeg', '.doc', '.docx', '.xls', '.xlsx'];
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(fileExt)) {
        setError(`File type ${fileExt} not allowed`);
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadDoc = async () => {
    if (isDemoMode) {
      setError('Cannot modify data in demo mode');
      return;
    }
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('document_type', docType);
    if (expirationDate) formData.append('expiration_date', expirationDate);
    if (notes) formData.append('notes', notes);

    try {
      const response = await fetch(`${API_URL}/horses/${horse.id}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      if (response.ok) {
        setSuccess('Document uploaded');
        resetDocForm();
        loadDocuments();
        if (onDocumentChange) onDocumentChange();
      } else {
        setError('Failed to upload document');
      }
    } catch (err) {
      setError('Error uploading document');
    } finally {
      setUploading(false);
    }
  };

  const resetDocForm = () => {
    setDocType('coggins');
    setSelectedFile(null);
    setExpirationDate('');
    setNotes('');
    setShowDocForm(false);
    const fileInput = document.getElementById(`file-input-${horse.id}`);
    if (fileInput) fileInput.value = '';
  };

  const viewDocument = async (doc) => {
    try {
      const response = await fetch(`${API_URL}/documents/${doc.id}/download`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setViewingDocument({ ...doc, url });
      }
    } catch (err) {
      setError('Error viewing document');
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await fetch(`${API_URL}/documents/${doc.id}/download`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.original_filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      setError('Error downloading document');
    }
  };

  const deleteDoc = async (id) => {
    if (isDemoMode) {
      setError('Cannot modify data in demo mode');
      return;
    }
    if (!window.confirm('Delete this document?')) return;

    try {
      const response = await fetch(`${API_URL}/documents/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        setSuccess('Document deleted');
        loadDocuments();
        if (onDocumentChange) onDocumentChange();
      }
    } catch (err) {
      setError('Error deleting document');
    }
  };

  const isExpired = (date) => date && new Date(date) < new Date();
  const isExpiringSoon = (date) => {
    if (!date) return false;
    const daysUntil = Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
    return daysUntil <= 30 && daysUntil >= 0;
  };

  return (
    <>
      {/* Horse Card */}
      <div style={{
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {/* Card Header - Always Visible */}
        <div
          onClick={onToggle}
          style={{
            padding: '20px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: isExpanded ? '#f9fafb' : 'white',
            borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none'
          }}
        >
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
              {horse.name}
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {getSummary()}
            </p>
          </div>
          {isExpanded ? <ChevronUp size={24} color="#6b7280" /> : <ChevronDown size={24} color="#6b7280" />}
        </div>

        {/* Card Body - Visible When Expanded */}
        {isExpanded && (
          <div style={{ padding: '20px' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
            ) : (
              <div style={{ display: 'grid', gap: '24px' }}>
                {/* FEEDING SECTION */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                      📋 Feeding {feeding.length > 0 && `(${feeding.length})`}
                    </h3>
                    {!showFeedingForm && (
                      <button onClick={() => setShowFeedingForm(true)} style={{ padding: '6px 12px', background: '#ea580c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem' }}>
                        <PlusCircle size={14} style={{ display: 'inline', marginRight: '4px' }} />
                        Add
                      </button>
                    )}
                  </div>

                  {showFeedingForm && (
                    <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '6px', marginBottom: '12px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Category *</label>
                          <select value={feedType} onChange={(e) => setFeedType(e.target.value)} required style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}>
                            <option value="">Select...</option>
                            <option value="Feed">Feed</option>
                            <option value="Hay">Hay</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>
                            Type {feedType === 'Feed' ? '(e.g., Sweet Feed, Grain)' : feedType === 'Hay' ? '(e.g., Alfalfa, Timothy)' : ''}
                          </label>
                          <input type="text" value={feedTypeDetail} onChange={(e) => setFeedTypeDetail(e.target.value)} placeholder={feedType === 'Feed' ? 'e.g., Sweet Feed' : feedType === 'Hay' ? 'e.g., Alfalfa' : 'Type of feed/hay'} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Amount</label>
                          <input type="text" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g., 2 flakes, 3 lbs" style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Frequency</label>
                          <input type="text" value={frequency} onChange={(e) => setFrequency(e.target.value)} placeholder="e.g., 2x daily" style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Schedule</label>
                          <input type="text" value={schedule} onChange={(e) => setSchedule(e.target.value)} placeholder="e.g., 7am, 7pm" style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Special Instructions</label>
                        <textarea value={specialInstructions} onChange={(e) => setSpecialInstructions(e.target.value)} rows={2} placeholder="Additional notes..." style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'inherit' }} />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={handleSaveFeeding} style={{ padding: '8px 16px', background: '#ea580c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>Save</button>
                        <button onClick={resetFeedingForm} style={{ padding: '8px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {feeding.length === 0 && !showFeedingForm ? (
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>No feeding instructions</div>
                  ) : (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {feeding.map(feed => (
                        <div key={feed.id} style={{
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderLeft: '4px solid #ea580c',
                          borderRadius: '6px',
                          padding: '12px',
                          fontSize: '0.875rem'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', marginBottom: '4px' }}>{feed.feed_type}</div>
                              {feed.amount && <div>Amount: {feed.amount}</div>}
                              {feed.frequency && <div>Frequency: {feed.frequency}</div>}
                              {feed.schedule && <div>Schedule: {feed.schedule}</div>}
                              {feed.special_instructions && <div>Notes: {feed.special_instructions}</div>}
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button onClick={() => editFeeding(feed)} style={{ padding: '4px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                <Edit2 size={14} />
                              </button>
                              <button onClick={() => deleteFeeding(feed.id)} style={{ padding: '4px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* MEDICATIONS SECTION */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                      💊 Medications {medications.filter(m => m.active).length > 0 && `(${medications.filter(m => m.active).length} active)`}
                    </h3>
                    {!showMedForm && (
                      <button onClick={() => setShowMedForm(true)} style={{ padding: '6px 12px', background: '#ea580c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem' }}>
                        <PlusCircle size={14} style={{ display: 'inline', marginRight: '4px' }} />
                        Add
                      </button>
                    )}
                  </div>

                  {showMedForm && (
                    <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '6px', marginBottom: '12px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Medication *</label>
                          <input type="text" value={medName} onChange={(e) => setMedName(e.target.value)} required style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Dosage</label>
                          <input type="text" value={dosage} onChange={(e) => setDosage(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Frequency</label>
                          <input type="text" value={medFrequency} onChange={(e) => setMedFrequency(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Purpose</label>
                          <input type="text" value={purpose} onChange={(e) => setPurpose(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Start Date</label>
                          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>End Date</label>
                          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Prescribing Vet</label>
                        <input type="text" value={prescribingVet} onChange={(e) => setPrescribingVet(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input type="checkbox" checked={medActive} onChange={(e) => setMedActive(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                          <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Currently Active</span>
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={handleSaveMed} style={{ padding: '8px 16px', background: '#ea580c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>Save</button>
                        <button onClick={resetMedForm} style={{ padding: '8px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {medications.length === 0 && !showMedForm ? (
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>No medications</div>
                  ) : (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {medications.map(med => (
                        <div key={med.id} style={{
                          background: '#f9fafb',
                          border: `1px solid ${med.active ? '#10b981' : '#e5e7eb'}`,
                          borderLeft: `4px solid ${med.active ? '#10b981' : '#9ca3af'}`,
                          borderRadius: '6px',
                          padding: '12px',
                          fontSize: '0.875rem'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {med.medication_name}
                                {med.active ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 6px', background: '#d1fae5', color: '#065f46', borderRadius: '4px', fontSize: '0.75rem' }}>
                                    <Check size={10} /> Active
                                  </span>
                                ) : (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 6px', background: '#f3f4f6', color: '#6b7280', borderRadius: '4px', fontSize: '0.75rem' }}>
                                    <X size={10} /> Inactive
                                  </span>
                                )}
                              </div>
                              {med.dosage && <div>Dosage: {med.dosage}</div>}
                              {med.frequency && <div>Frequency: {med.frequency}</div>}
                              {med.purpose && <div>Purpose: {med.purpose}</div>}
                              {(med.start_date || med.end_date) && (
                                <div>
                                  {med.start_date && `Start: ${new Date(med.start_date).toLocaleDateString()}`}
                                  {med.start_date && med.end_date && ' • '}
                                  {med.end_date && `End: ${new Date(med.end_date).toLocaleDateString()}`}
                                </div>
                              )}
                              {med.prescribing_vet && <div>Vet: {med.prescribing_vet}</div>}
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button onClick={() => editMed(med)} style={{ padding: '4px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                <Edit2 size={14} />
                              </button>
                              <button onClick={() => deleteMed(med.id)} style={{ padding: '4px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* SUPPLEMENTS SECTION */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                      🌿 Supplements {supplements.filter(s => s.active).length > 0 && `(${supplements.filter(s => s.active).length} active)`}
                    </h3>
                    {!showSuppForm && (
                      <button onClick={() => setShowSuppForm(true)} style={{ padding: '6px 12px', background: '#ea580c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem' }}>
                        <PlusCircle size={14} style={{ display: 'inline', marginRight: '4px' }} />
                        Add
                      </button>
                    )}
                  </div>

                  {showSuppForm && (
                    <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '6px', marginBottom: '12px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Supplement *</label>
                          <input type="text" value={suppName} onChange={(e) => setSuppName(e.target.value)} required style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Dosage</label>
                          <input type="text" value={suppDosage} onChange={(e) => setSuppDosage(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Frequency</label>
                          <input type="text" value={suppFrequency} onChange={(e) => setSuppFrequency(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Purpose</label>
                          <input type="text" value={suppPurpose} onChange={(e) => setSuppPurpose(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input type="checkbox" checked={suppActive} onChange={(e) => setSuppActive(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                          <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Currently Active</span>
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={handleSaveSupp} style={{ padding: '8px 16px', background: '#ea580c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>Save</button>
                        <button onClick={resetSuppForm} style={{ padding: '8px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {supplements.length === 0 && !showSuppForm ? (
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>No supplements</div>
                  ) : (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {supplements.map(supp => (
                        <div key={supp.id} style={{
                          background: '#f9fafb',
                          border: `1px solid ${supp.active ? '#10b981' : '#e5e7eb'}`,
                          borderLeft: `4px solid ${supp.active ? '#10b981' : '#9ca3af'}`,
                          borderRadius: '6px',
                          padding: '12px',
                          fontSize: '0.875rem'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {supp.supplement_name}
                                {supp.active ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 6px', background: '#d1fae5', color: '#065f46', borderRadius: '4px', fontSize: '0.75rem' }}>
                                    <Check size={10} /> Active
                                  </span>
                                ) : (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 6px', background: '#f3f4f6', color: '#6b7280', borderRadius: '4px', fontSize: '0.75rem' }}>
                                    <X size={10} /> Inactive
                                  </span>
                                )}
                              </div>
                              {supp.dosage && <div>Dosage: {supp.dosage}</div>}
                              {supp.frequency && <div>Frequency: {supp.frequency}</div>}
                              {supp.purpose && <div>Purpose: {supp.purpose}</div>}
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button onClick={() => editSupp(supp)} style={{ padding: '4px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                <Edit2 size={14} />
                              </button>
                              <button onClick={() => deleteSupp(supp.id)} style={{ padding: '4px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* DOCUMENTS SECTION */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                      📄 Documents {documents.length > 0 && `(${documents.length})`}
                    </h3>
                    {!showDocForm && (
                      <button onClick={() => setShowDocForm(true)} style={{ padding: '6px 12px', background: '#ea580c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem' }}>
                        <Upload size={14} style={{ display: 'inline', marginRight: '4px' }} />
                        Upload
                      </button>
                    )}
                  </div>

                  {showDocForm && (
                    <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '6px', marginBottom: '12px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Document Type *</label>
                          <select value={docType} onChange={(e) => setDocType(e.target.value)} required style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}>
                            <option value="coggins">Coggins Test</option>
                            <option value="health_cert">Health Certificate</option>
                            <option value="vaccination">Vaccination Record</option>
                            <option value="registration">Registration Papers</option>
                            <option value="insurance">Insurance Document</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Expiration Date</label>
                          <input type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        </div>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>File * (PDF, PNG, JPG, DOC, XLSX - Max 10MB)</label>
                        <input id={`file-input-${horse.id}`} type="file" onChange={handleFileChange} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx" required style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                        {selectedFile && <div style={{ marginTop: '8px', fontSize: '0.875rem', color: '#6b7280' }}>Selected: {selectedFile.name}</div>}
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>Notes</label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'inherit' }} />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={handleUploadDoc} disabled={uploading || !selectedFile} style={{ padding: '8px 16px', background: '#ea580c', color: 'white', border: 'none', borderRadius: '4px', cursor: uploading || !selectedFile ? 'not-allowed' : 'pointer', fontWeight: '500', opacity: uploading || !selectedFile ? 0.6 : 1 }}>
                          {uploading ? 'Uploading...' : 'Upload'}
                        </button>
                        <button onClick={resetDocForm} style={{ padding: '8px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {documents.length === 0 && !showDocForm ? (
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>No documents</div>
                  ) : (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {documents.map(doc => {
                        const expired = isExpired(doc.expiration_date);
                        const expiringSoon = isExpiringSoon(doc.expiration_date);
                        return (
                          <div key={doc.id} style={{
                            background: '#f9fafb',
                            border: `1px solid ${expired ? '#ef4444' : expiringSoon ? '#f59e0b' : '#e5e7eb'}`,
                            borderLeft: `4px solid ${expired ? '#ef4444' : expiringSoon ? '#f59e0b' : '#3b82f6'}`,
                            borderRadius: '6px',
                            padding: '12px',
                            fontSize: '0.875rem'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  <FileText size={16} />
                                  <span style={{ fontWeight: '600' }}>{doc.document_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                  {(expired || expiringSoon) && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 6px', background: expired ? '#fee2e2' : '#fef3c7', color: expired ? '#991b1b' : '#92400e', borderRadius: '4px', fontSize: '0.75rem' }}>
                                      <AlertCircle size={10} /> {expired ? 'Expired' : 'Expiring Soon'}
                                    </span>
                                  )}
                                </div>
                                <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>{doc.original_filename}</div>
                                {doc.expiration_date && (
                                  <div style={{ color: expired ? '#ef4444' : expiringSoon ? '#f59e0b' : '#1f2937', fontWeight: expired || expiringSoon ? '600' : 'normal' }}>
                                    Expires: {new Date(doc.expiration_date).toLocaleDateString()}
                                  </div>
                                )}
                                {doc.notes && <div style={{ marginTop: '4px', color: '#6b7280' }}>{doc.notes}</div>}
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button onClick={() => viewDocument(doc)} style={{ padding: '4px 8px', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>
                                  <Eye size={14} style={{ display: 'inline', marginRight: '2px' }} />
                                  View
                                </button>
                                <button onClick={() => downloadDocument(doc)} style={{ padding: '4px 8px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>
                                  <Download size={14} style={{ display: 'inline', marginRight: '2px' }} />
                                  Download
                                </button>
                                <button onClick={() => deleteDoc(doc.id)} style={{ padding: '4px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <div onClick={() => setViewingDocument(null)} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white',
            borderRadius: '8px',
            maxWidth: '90%',
            maxHeight: '90%',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>{viewingDocument.original_filename}</h3>
              <button onClick={() => setViewingDocument(null)} style={{ padding: '8px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Close
              </button>
            </div>
            <div style={{ padding: '16px' }}>
              {viewingDocument.mime_type?.startsWith('image/') ? (
                <img src={viewingDocument.url} alt={viewingDocument.original_filename} style={{ maxWidth: '100%', height: 'auto' }} />
              ) : viewingDocument.mime_type === 'application/pdf' ? (
                <iframe src={viewingDocument.url} style={{ width: '800px', height: '600px', border: 'none', maxWidth: '100%' }} title="PDF Viewer" />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <p>Preview not available for this file type.</p>
                  <button onClick={() => downloadDocument(viewingDocument)} style={{ marginTop: '16px', padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    <Download size={16} style={{ display: 'inline', marginRight: '8px' }} />
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
